/**
 * The two lint rules that judge text against the clock: copy that never appears at all (an error) and
 * copy held too briefly to read (a warning). Both *measure* visibility — seeking the timeline and
 * reading rendered opacity — rather than trusting author-declared attributes, which HtmlAsset
 * compositions don't have. Reading time ports codeo's math (~15 chars/sec, 1.2s floor, ±0.05s
 * tolerance), converting the measured progress-span to seconds via the scene's real playback duration.
 */
import type { GsapTimelineLike } from './types';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';
import { samplePoses } from './scenePass';
import type { TextLeaf } from './textLeaves';
import { collectTextLeaves } from './textLeaves';

// A perceptual floor plus a ~15 chars/sec reading rate.
const MIN_READ_TIME_S = 1.2;
const READING_CHARS_PER_SEC = 15;

// Slack so a borderline window doesn't flip-flop between runs.
export const READING_TIME_TOLERANCE_S = 0.05;

/** Seconds a viewer needs to comfortably read `chars` characters. */
export function requiredReadTimeS(chars: number): number {
    return Math.max(MIN_READ_TIME_S, 0.4 + chars / READING_CHARS_PER_SEC);
}

/** Whether text of `chars` shown for `visibleSeconds` is too brief to read (within tolerance). */
export function readsTooFast(visibleSeconds: number, chars: number): boolean {
    return visibleSeconds + READING_TIME_TOLERANCE_S < requiredReadTimeS(chars);
}

/**
 * Longest contiguous visible span in progress units (0..1) — the first-to-last progress of the run.
 * Multiple appearances yield the longest run; a single-sample flash and empty input yield 0.
 */
export function longestVisibleSpanProgress(
    samples: ReadonlyArray<{ progress: number; visible: boolean }>,
): number {
    let longest = 0;
    let runStart: number | null = null;
    for (const { progress, visible } of samples) {
        if (visible) {
            if (runStart === null) {
                runStart = progress;
            }
            longest = Math.max(longest, progress - runStart);
        } else {
            runStart = null;
        }
    }
    return longest;
}

interface VisibilitySample {
    progress: number;
    visible: boolean;
}

export interface LeafVisibility {
    leaf: TextLeaf;
    samples: VisibilitySample[];
    /**
     * The leaf's copy at the end of the scene. A counter or typewriter rewrites its text as it
     * plays, so what it finally says is the copy the viewer is meant to read.
     */
    settledText: string;
    /** The same for the whole line the leaf sits in: a word styled as its own span is still read as part of its sentence. */
    settledLine: string;
    /**
     * Whether `visibility` alone kept it from painting at every sampled moment, at full opacity:
     * the source a particle or fragment effect is built from, never itself on screen.
     */
    hiddenThroughout: boolean;
}

/**
 * A `window.__timelines.main` these rules can actually measure. The composition declares that object
 * itself, so it can be anything — a stub with only `progress`, or a timeline from another GSAP build —
 * and a rule that assumed otherwise would throw and take every finding of the run down with it.
 * `measurable === false` is the host saying so outright: the composition registered a timeline whose
 * clock it could not read, and the zeros it reports instead are not facts to judge text against.
 */
function measurableTimeline(context: RuntimeLintContext): GsapTimelineLike | null {
    const { timeline } = context;
    if (!timeline || typeof timeline.duration !== 'function' || timeline.measurable === false) {
        return null;
    }
    return timeline;
}

// Both rules below judge text against the clock, and the runtime rules run in sequence against one
// loaded composition — so the pass is memoized on the context and whichever rule asks second reuses
// it. The asset builds a fresh context per lint, so the cache can't go stale.
const visibilityByContext = new WeakMap<RuntimeLintContext, Promise<LeafVisibility[]>>();

export function sampleTextVisibility(context: RuntimeLintContext): Promise<LeafVisibility[]> {
    const cached = visibilityByContext.get(context);
    if (cached) {
        return cached;
    }
    const pass = (async () => {
        const timeline = measurableTimeline(context);
        const leaves =
            timeline && timeline.duration() > 0 ? collectTextLeaves(context.document) : [];
        if (leaves.length === 0) {
            return [];
        }
        const scene = await samplePoses(context);
        if (!scene) {
            return [];
        }
        // The leaves were gathered at the starting frame, so they describe nodes that are in none of
        // the posed documents — a sandboxed pose returns a different one each time. Each pose
        // recorded what it saw against the element index, which is what pairs a leaf with itself;
        // pairing on position in the leaf list would be wrong, because copy that types itself in
        // joins the list partway through the scene and shifts every leaf after it.
        //
        // A leaf missing from a pose rendered no copy of its own at that frame, which is what "not
        // legible" means. Recording that rather than dropping the sample keeps every leaf on one
        // grid, so a visible span cannot be credited across a gap and the interval the reading-time
        // padding derives from stays the real one.
        return leaves.map((leaf) => {
            const settled = scene.settledByIndex.get(leaf.index);
            return {
                leaf,
                samples: scene.poses.map(({ progress, legibleByIndex }) => ({
                    progress,
                    visible: legibleByIndex.get(leaf.index) ?? false,
                })),
                settledText: settled?.text ?? leaf.preview,
                settledLine: settled?.line ?? leaf.preview,
                hiddenThroughout: scene.poses.every(({ hiddenByIndex }) =>
                    hiddenByIndex.has(leaf.index),
                ),
            };
        });
    })();
    visibilityByContext.set(context, pass);
    return pass;
}

/** Copy legible at no sampled moment — it never renders, or renders too briefly to be read. */
export function neverVisible(entry: LeafVisibility): boolean {
    return !entry.samples.some((sample) => sample.visible);
}

/**
 * Copy the viewer never sees, where the graphic says something. No other rule catches it: the
 * geometry and contrast rules skip elements that measure invisible, so a composition can lose most of
 * its content and still lint clean. Scoped to the same non-ambient text leaves reading time judges —
 * an element with no copy of its own may be a deliberately hidden layer.
 */
export const textNeverVisible: RuntimeLintRule = {
    id: 'text-never-visible',
    severity: 'error',
    guidance:
        'Every piece of on-screen copy must appear, and hold long enough to read. The usual way it ' +
        'fails to: a build-time `gsap.set(el, { opacity: 0 })` under an entrance `from`/`fromTo` that ' +
        'starts at `opacity: 0` cancels itself out — `from` animates to the element’s current value, ' +
        'so the tween runs 0 → 0. Drop the `set` (a hidden from-state already holds the element back) ' +
        'or give the entrance a visible end-state with `fromTo`.',
    async check(context: RuntimeLintContext): Promise<string[]> {
        const visibility = await sampleTextVisibility(context);
        // Copy is its words: a fragment effect's clones each crop the word to their own piece,
        // and one showing none of it is not the copy going missing while another shows it.
        const shownWords = new Set(
            visibility.filter((entry) => !neverVisible(entry)).map((entry) => entry.settledText),
        );
        const reported = new Set<string>();
        const missing = visibility.filter((entry) => {
            if (!neverVisible(entry) || entry.hiddenThroughout) return false;
            if (shownWords.has(entry.settledText) || reported.has(entry.settledText)) return false;
            reported.add(entry.settledText);
            return true;
        });
        return missing.map(({ leaf }) => {
            const preview =
                leaf.preview.length > 40 ? `${leaf.preview.slice(0, 37)}…` : leaf.preview;
            return (
                `"${preview}" is never on screen to be read. Either its entrance never lands — a ` +
                `build-time \`gsap.set(el, { opacity: 0 })\` under a \`from\`/\`fromTo\` from ` +
                `\`opacity: 0\` runs 0 → 0, so drop the \`set\` or give the entrance a visible ` +
                `end-state with \`fromTo\` — or it flashes past in under a third of a second, in ` +
                `which case hold it long enough to read.`
            );
        });
    },
};

/**
 * The copy a viewer reads, a line at a time: the letters a word is built from and the clones a
 * fragment effect crops it into are one line, legible whenever any of them is. Copy that shows at
 * no sampled moment is text-never-visible's error, not a reading window, and is left out.
 */
function linesOf(
    visibility: readonly LeafVisibility[],
): { copy: string; samples: VisibilitySample[] }[] {
    const byLine = new Map<string, VisibilitySample[]>();
    for (const entry of visibility) {
        if (neverVisible(entry)) {
            continue;
        }
        const line = byLine.get(entry.settledLine);
        byLine.set(
            entry.settledLine,
            line
                ? line.map((sample, index) => ({
                      progress: sample.progress,
                      visible: sample.visible || entry.samples[index]?.visible === true,
                  }))
                : entry.samples,
        );
    }
    return [...byLine].map(([copy, samples]) => ({ copy, samples }));
}

/**
 * Flags text whose longest legible span is shorter than its read time, via a dense seek-and-measure
 * pass converted to seconds with the real `sceneDurationMs`. Warning severity. Returns [] when it
 * can't judge honestly: no real duration, no seekable timeline, or no text.
 */
export const textReadingTime: RuntimeLintRule = {
    id: 'text-reading-time',
    severity: 'warning',
    guidance:
        'Hold readable text on screen long enough to actually read (≈15 chars/sec, min ~1.2s). If a ' +
        'piece is shown too briefly: when the scene’s duration is flexible (text-on-media) lengthen ' +
        'the scene; when it is fixed by a voiceover, simplify the design — fewer words, larger, or ' +
        'sequence pieces so each holds longer. Mark genuinely-ambient text (watermarks, kinetic ' +
        'background motifs) `data-l5-text-role="ambient"` to waive this.',
    async check(context: RuntimeLintContext): Promise<string[]> {
        const { sceneDurationMs } = context;
        if (
            typeof sceneDurationMs !== 'number' ||
            !Number.isFinite(sceneDurationMs) ||
            sceneDurationMs <= 0
        ) {
            return [];
        }
        const sceneDurationSeconds = sceneDurationMs / 1000;

        const messages: string[] = [];
        for (const { copy, samples } of linesOf(await sampleTextVisibility(context))) {
            // A run undercounts the true window by up to half a sample interval each side (visibility
            // starts/ends between samples). Add one interval back so a genuinely-long-enough window
            // isn't flagged — biasing the heuristic away from false positives.
            const sampleInterval = 1 / (samples.length - 1);
            const spanProgress = Math.min(1, longestVisibleSpanProgress(samples) + sampleInterval);
            const spanSeconds = spanProgress * sceneDurationSeconds;
            if (!readsTooFast(spanSeconds, copy.length)) {
                continue;
            }
            const preview = copy.length > 40 ? `${copy.slice(0, 37)}…` : copy;
            messages.push(
                `"${preview}" (${copy.length} chars) is on screen for only ~${spanSeconds.toFixed(2)}s ` +
                    `but needs ~${requiredReadTimeS(copy.length).toFixed(2)}s to read comfortably. Hold ` +
                    `it longer (lengthen the scene if its duration is flexible, or sequence it so it ` +
                    `stays up longer), shorten the copy, or mark it data-l5-text-role="ambient" if it ` +
                    `is decorative and not meant to be read.`,
            );
        }
        return messages;
    },
};
